import React, { useState, useEffect, useRef } from "react";
import { Formik, Form } from "formik";
import { useTranslation } from "react-i18next";
import { serialize } from "object-to-formdata";
import { useSelector } from "react-redux";
import { useLocation, useNavigate  } from "react-router-dom";

import Button from "components/common/Button";
import ShadowBlock from "components/common/Layout/ShadowBlock";

import ExchangeTransport, { initializeTransportFields } from "models/exchangeTransport";
import { validationSchema } from "./validate";
import { TRANSPORT } from "helpers/routes";
import { useHttp } from "hooks/http.hook";
import { useInitialDocument } from "hooks/documents";
import { TransportService } from "service/TransportService";
import { TRANSPORT as TRANSPORT_API } from "endpointsConst";

import AddCompanyInfo from "components/Forms/AddCompanyInfoWithEmployee";
import EchangeRoutes from "components/TransportComponents/ExchangeRoutes";
import GeneralInformation from "components/TransportComponents/GeneralInformation";
import TransportBody from "components/TransportComponents/TransportBody";
import TransportSizes from "components/TransportComponents/TransportSizes";
import Payment from "components/TransportComponents/Payment";
import Loading from "components/Loading";
import Error from "components/Forms/Error";
import { MODE_TRANSPORT } from "constants/transport";
import { sendAim } from "helpers/statisticAim";

import BackButton from "components/common/BackButton";
import styles from "./styles.module.scss";

const REGEXP_TRANSPORT = /transport/;
const REGEXP_ID = /id/;

const AddToExchange = () => {
    const [initValues, setInitValues] = useState(initializeTransportFields);
    const { t } = useTranslation();
    const location = useLocation();
    const transportIdQuery = location.search
        .split("&")
        .filter((el) => REGEXP_TRANSPORT.test(el))[0];
    const exchangeIdQuery = location.search.split("&").filter((el) => REGEXP_ID.test(el))[0];
    const refTransportId = useRef(null);
    const { request, loading } = useHttp();
    const navigate = useNavigate();
    const lang = useSelector((state) => state.settings.lang);
    const { fetchDocumnets } = useInitialDocument();

    useEffect(() => {
        if (transportIdQuery) {
            const transportId = transportIdQuery.split("=")[1];
            if (refTransportId.current !== transportId && transportId) {
                refTransportId.current = transportId;
                TransportService.getTrackParkTransportData(transportId, lang)
                    .then(async (res) => {
                        // Handle both response formats: direct object or wrapped in items array
                        const rawData = res.data.items ? res.data.items[0] : res.data;
                        const transportData = ExchangeTransport.initFormData(rawData);
                        if (transportData.transport_technical_documents) {
                            try {
                                transportData.transport_technical_documents = await fetchDocumnets(
                                    transportData.transport_technical_documents,
                                );
                            } catch (e) {}
                        }
                        transportData.transport_ids = transportId;
                        setInitValues(transportData);
                    })
                    .catch((e) => {
                        console.log(e);
                        setInitValues({
                            ...initializeTransportFields,
                            transport_ids: transportId,
                        });
                    });
            }
        } else if (exchangeIdQuery) {
            const exchangeId = exchangeIdQuery.split("=")[1];
            TransportService.getById(exchangeId, { lang })
                .then(async (res) => {
                    const exchange = ExchangeTransport.initFormData(res.data);
                    if (exchange.transport_technical_documents) {
                        try {
                            exchange.transport_technical_documents = await fetchDocumnets(
                                exchange.transport_technical_documents,
                            );
                        } catch (e) {}
                    }
                    exchange.id = res.data.id;
                    setInitValues(exchange);
                })
                .catch((e) => {});
        } else {
            setInitValues(initializeTransportFields);
        }
    }, [exchangeIdQuery, fetchDocumnets, lang, transportIdQuery]);

    return (
        <>
        <BackButton />
        <Formik
            initialValues={initValues}
            validationSchema={validationSchema(t)}
            enableReinitialize={true}
            onSubmit={async (formValues, { setSubmitting, setErrors }) => {
                setSubmitting(false);
                const exchangeTransport = ExchangeTransport.formData(formValues);
                const formData = serialize(exchangeTransport, { indices: true });
                formValues.transport_technical_documents &&
                    formValues.transport_technical_documents.forEach((el) => {
                        formData.append("document", el);
                    });

                const exchangeId = exchangeIdQuery ? exchangeIdQuery.split("=")[1] : null;
                const url = exchangeId
                    ? `${TRANSPORT_API}/exchange/${exchangeId}`
                    : `${TRANSPORT_API}/exchange`;
                await request(
                    url,
                    exchangeIdQuery ? "patch" : "post",
                    formData
                )
                    .then((res) => {
                        navigate(`${TRANSPORT.search}?status=active`);
                        if (!exchangeIdQuery) {
                            sendAim("trans_add");
                        }
                    })
                    .catch((e) => {
                        const errors = {};
                        e.response.data.errors.forEach((error) => {
                            const errorPath =
                                error.path[0] === "document"
                                    ? "transport_technical_documents"
                                    : error.path[0];
                            errors[errorPath] = t(error.type);
                        });
                        setErrors(errors);
                    });
            }}
        >
            {({ submitCount, errors }) => (
                <Form>
                    <ShadowBlock>
                        <GeneralInformation mode={MODE_TRANSPORT.exchange} setFieldValue={setInitValues} />
                        <TransportBody />
                        <TransportSizes mode={MODE_TRANSPORT.exchange} />
                        <EchangeRoutes />
                        <Payment />
                    </ShadowBlock>
                    <AddCompanyInfo />

                    <div className={styles.form__footer}>
                        {submitCount > 0 && Object.entries(errors).length > 0 && (
                            <Error>{t("errors.mainFormError")}</Error>
                        )}
                        {loading && (
                            <div className={styles.loading}>
                                <Loading />
                            </div>
                        )}
                        <div className={styles.button__continue}>
                            <Button disabled={loading} type="submit">
                                {exchangeIdQuery ? t("edit_save") : t("add")}
                            </Button>
                        </div>
                    </div>
                </Form>
            )}
        </Formik>
        </>
    );
};

export default AddToExchange;
